import React from "react";
import { FormCard } from "@/components/FormCard";
import { ContentPageTheme, Form, ReviewWidgetConfig, ReviewWidgetConfigServices } from "@/services/bedrock";
import { SettingGroup, SettingRow, SortSettingRow } from "../../components/SettingRow";
import { ThemeAndFormPickers } from "../../components/ThemeAndFormPickers";
import { SaveCancelActions } from "../../components/SaveCancelActions";
import { useSavedConfig } from "../../components/useSavedConfig";

type EditableConfig = Pick<
  ReviewWidgetConfig,
  | "showStarSummary"
  | "showSentimentSummary"
  | "showNpsSummary"
  | "formId"
  | "contentPageThemeId"
  | "reviewSortType"
  | "useCondensedQuotes"
  | "approvedForSocialOnly"
  | "autoScrollEnabled"
>;

const getEditableConfig = (config: ReviewWidgetConfig): EditableConfig => ({
  showStarSummary: config.showStarSummary,
  showSentimentSummary: config.showSentimentSummary,
  showNpsSummary: config.showNpsSummary,
  formId: config.formId ?? null,
  contentPageThemeId: config.contentPageThemeId ?? null,
  reviewSortType: config.reviewSortType,
  useCondensedQuotes: config.useCondensedQuotes,
  approvedForSocialOnly: config.approvedForSocialOnly,
  autoScrollEnabled: config.autoScrollEnabled,
});

interface Props {
  config: ReviewWidgetConfig;
  updateConfig: (updates: Partial<ReviewWidgetConfig>) => void;
  themes: ContentPageTheme[];
  forms: Form[];
}

const ReviewWidgetConfigForm: React.FC<Props> = ({ config, updateConfig, themes, forms }) => {
  const { hasChanges, isSaving, handleCancel, handleSave } = useSavedConfig({
    config,
    getEditable: getEditableConfig,
    updateConfig,
    save: payload => ReviewWidgetConfigServices.updateConfig(config.id, payload),
    savedMessage: "Review widget settings saved",
    errorMessage: "Error saving review widget settings",
  });

  return (
    <FormCard>
      <FormCard.Header
        title="Configurations"
        description="Configure and preview the embeddable review widget for Player websites."
      />

      <FormCard.Content>
        <div className="grid gap-4 @min-[35rem]:grid-cols-2">
          <SettingGroup title="Summaries">
            <SettingRow
              id="show-star-summary"
              label="Star summary"
              tooltip="Hidden on the widget if the Player's average star rating is below 4.0."
              checked={config.showStarSummary}
              onCheckedChange={checked => updateConfig({ showStarSummary: checked })}
            />
            <SettingRow
              id="show-sentiment-summary"
              label="Sentiment summary"
              tooltip="Hidden on the widget if fewer than 70% of reviews are positive."
              checked={config.showSentimentSummary}
              onCheckedChange={checked => updateConfig({ showSentimentSummary: checked })}
            />
            <SettingRow
              id="show-nps-summary"
              label="NPS summary"
              tooltip="Hidden on the widget if the Player's NPS is below 50."
              checked={config.showNpsSummary}
              onCheckedChange={checked => updateConfig({ showNpsSummary: checked })}
            />
          </SettingGroup>

          <SettingGroup title="Reviews">
            <SettingRow
              id="use-condensed-quotes"
              label="Condensed quotes"
              checked={config.useCondensedQuotes}
              onCheckedChange={checked => updateConfig({ useCondensedQuotes: checked })}
            />
            <SettingRow
              id="approved-for-social-only"
              label="Approved for social only"
              checked={config.approvedForSocialOnly}
              onCheckedChange={checked => updateConfig({ approvedForSocialOnly: checked })}
            />
            <SettingRow
              id="auto-scroll-enabled"
              label="Auto scroll"
              checked={config.autoScrollEnabled}
              onCheckedChange={checked => updateConfig({ autoScrollEnabled: checked })}
            />
            <SortSettingRow
              value={config.reviewSortType}
              onChange={reviewSortType => updateConfig({ reviewSortType })}
            />
          </SettingGroup>
        </div>

        <ThemeAndFormPickers
          themes={themes}
          forms={forms}
          contentPageThemeId={config.contentPageThemeId}
          formId={config.formId}
          onChange={updateConfig}
        />
      </FormCard.Content>

      <SaveCancelActions hasChanges={hasChanges} isSaving={isSaving} onCancel={handleCancel} onSave={handleSave} />
    </FormCard>
  );
};

export { ReviewWidgetConfigForm };
